/**
 * seatLabel.ts — Traductor de IDs de asientos a etiquetas legibles
 *
 * Convierte identificadores técnicos del SVG en texto para auxiliares y staff.
 *
 * Formatos reconocidos:
 *   - "seat-T57-4"  → { mesa: "57", asiento: "4" }   → "Mesa: 57, Asiento: 4"
 *   - "seat-57-4"   → { mesa: "57", asiento: "4" }   → "Mesa: 57, Asiento: 4"
 *
 * Para agregar un nuevo formato en el futuro: añadir un objeto SeatIdParser
 * al array SEAT_ID_PARSERS siguiendo la misma interfaz.
 */

import type { Mapping } from '@/types/mapping';

/** Resultado normalizado de parsear un seatId */
export type SeatSegments = {
  /** Número de mesa (ej: "57" extraído de "seat-T57-4") */
  mesa?: string;
  /** Número de asiento dentro de la mesa (ej: "4" extraído de "seat-T57-4") */
  asiento?: string;
  /** ID original sin procesar, siempre presente */
  raw: string;
};

interface SeatIdParser {
  /** Nombre del formato, útil para debugging y logs */
  name: string;
  /** Retorna true si este parser reconoce el formato del id */
  matches: (id: string) => boolean;
  /** Parsea el id y devuelve los segmentos normalizados */
  parse: (id: string) => SeatSegments;
}

/**
 * Registro de parsers por formato de seatId.
 * Evaluados en orden: del más específico al más genérico.
 *
 * Para soportar un nuevo formato SVG en el futuro, agregar aquí un nuevo
 * objeto que implemente SeatIdParser sin modificar el resto de la utilidad.
 */
const SEAT_ID_PARSERS: SeatIdParser[] = [
  /**
   * Formato: seat-T{N}-{asiento}
   * Ejemplo: "seat-T57-4" → mesa="57", asiento="4"
   * Contexto: SVGs generados por el editor interno del backoffice.
   *           La "T" es un prefijo del SVG; para mostrar solo se usa el número.
   */
  {
    name: 'seat_hyphen_T_numeric',
    matches: (id) => /^seat-T\d+-\d+$/i.test(id),
    parse: (id) => {
      const m = id.match(/^seat-T(\d+)-(\d+)$/i);
      return { mesa: m?.[1], asiento: m?.[2], raw: id };
    },
  },

  /**
   * Formato: seat-{N}-{asiento}  (sin prefijo T)
   * Ejemplo: "seat-57-4" → mesa="57", asiento="4"
   * Contexto: SVGs con numeración directa sin prefijo de tabla.
   */
  {
    name: 'seat_hyphen_numeric',
    matches: (id) => /^seat-\d+-\d+$/i.test(id),
    parse: (id) => {
      const m = id.match(/^seat-(\d+)-(\d+)$/i);
      return { mesa: m?.[1], asiento: m?.[2], raw: id };
    },
  },
];

/** Usado cuando ningún parser registrado reconoce el formato */
const FALLBACK_PARSER: SeatIdParser = {
  name: 'unknown',
  matches: () => true,
  parse: (id) => ({ raw: id }),
};

/**
 * Parsea un seatId y devuelve sus segmentos normalizados.
 * Si ningún parser lo reconoce, devuelve solo `{ raw: id }`.
 */
export function parseSeatId(seatId: string): SeatSegments {
  const parser = SEAT_ID_PARSERS.find((p) => p.matches(seatId)) ?? FALLBACK_PARSER;
  return parser.parse(seatId);
}

/** Opciones de formato para la etiqueta de salida */
export type SeatLabelOptions = {
  /** Separador entre segmentos. Por defecto: ", " */
  separator?: string;
  /**
   * Textos de etiqueta por segmento.
   * Por defecto: { mesa: "Mesa", asiento: "Asiento" }
   * Útil para adaptar a otros idiomas o contextos (ej: boletería, impresión).
   */
  labels?: {
    mesa?: string;
    asiento?: string;
  };
};

const DEFAULT_LABELS = { mesa: 'Mesa', asiento: 'Asiento' } as const;

/**
 * Convierte un seatId en texto legible para auxiliares y staff.
 *
 * @param seatId   ID crudo del asiento (ej: "seat-T57-4")
 * @param mapping  mapping_json del mapa (opcional). Si se pasa, se usa el label
 *                 guardado en el mapping en vez del número extraído del ID.
 *                 Útil cuando las mesas tienen nombres personalizados (ej: "VIP").
 * @param options  Separador y etiquetas personalizables
 *
 * @example
 * formatSeatLabel('seat-T57-4')
 * // → "Mesa: 57, Asiento: 4"
 *
 * formatSeatLabel('seat-T57-4', mappingJson)
 * // → "Mesa: VIP, Asiento: 4"  (si el mapping tiene label="VIP" para esa mesa)
 *
 * // Formato para impresión de ticket:
 * formatSeatLabel('seat-T57-4', null, { separator: ' | ' })
 * // → "Mesa: 57 | Asiento: 4"
 */
export function formatSeatLabel(
  seatId: string,
  mapping?: Mapping | null,
  options: SeatLabelOptions = {}
): string {
  const { separator = ', ', labels = {} } = options;
  const l = { ...DEFAULT_LABELS, ...labels };

  const segments = parseSeatId(seatId);

  // Por defecto usar los segmentos del ID
  let mesaLabel = segments.mesa;
  let asientoLabel = segments.asiento;

  // Si hay mapping, enriquecer con los labels configurados en el mapa
  if (mapping) {
    for (const table of mapping.tables) {
      const seat = table.seats.find((s) => s.id === seatId);
      if (seat) {
        mesaLabel = table.label || mesaLabel;
        asientoLabel = seat.label || asientoLabel;
        break;
      }
    }
  }

  const parts: string[] = [];
  if (mesaLabel) parts.push(`${l.mesa}: ${mesaLabel}`);
  if (asientoLabel) parts.push(`${l.asiento}: ${asientoLabel}`);

  // Fallback: si no se pudo parsear ningún segmento, devolver el ID crudo
  return parts.length > 0 ? parts.join(separator) : seatId;
}
